import type { SvgIconComponent } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockClockIcon from '@mui/icons-material/LockClock';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, Typography, useTheme } from '@mui/material';

import type { SecretStatus } from '@/types';

type ErrorConfigItem = {
    icon: SvgIconComponent;
    title: string;
    message: string;
    /** error: something failed (red); outcome: the secret works as set up and just can't be opened now (grey) */
    tone: 'error' | 'outcome';
};

type NullableString = string | null | undefined;

interface ErrorDisplayProps {
    status?: SecretStatus;
    title?: NullableString;
    message?: NullableString;
    onRetry?: () => void;
}

const defaultErrorConfig: ErrorConfigItem = {
    icon: ErrorOutlineIcon,
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again later.',
    tone: 'error',
};

// Each one says what happened and what to do next: in every case, the sender can share it again.
const errorConfig: Partial<Record<SecretStatus, ErrorConfigItem>> = {
    not_found: {
        icon: SearchOffIcon,
        title: 'Secret not found',
        message:
            "This link doesn't lead to a secret. Check that you copied the whole link, or ask the sender for a new one.",
        tone: 'outcome',
    },
    expired: {
        icon: AccessTimeIcon,
        title: 'Link expired',
        message: "This link has expired, so the secret can't be opened anymore. Ask the sender to share it again.",
        tone: 'outcome',
    },
    already_viewed: {
        icon: VisibilityOffIcon,
        title: 'Already opened',
        message:
            'This link could be opened only once, and it already was. If you still need the secret, ask the sender to share it again.',
        tone: 'outcome',
    },
    erased: {
        icon: DeleteOutlineIcon,
        title: 'Secret deleted',
        message: 'The sender deleted this secret. Ask them to share it again if you still need it.',
        tone: 'outcome',
    },
    unavailable: {
        icon: LockClockIcon,
        title: 'Not available right now',
        message: "This secret can't be opened right now. Contact the person who sent it.",
        tone: 'outcome',
    },
    error: defaultErrorConfig,
};

const resolveConfig = (
    status: SecretStatus | undefined,
    overrides: { title?: NullableString; message?: NullableString }
): { config: ErrorConfigItem; resolvedTitle: string; resolvedMessage: string } => {
    const resolvedStatus: SecretStatus = status ?? 'error';
    const config = errorConfig[resolvedStatus] ?? defaultErrorConfig;

    const overrideTitle = overrides.title ?? undefined;
    const overrideMessage = overrides.message ?? undefined;

    return {
        config,
        resolvedTitle: overrideTitle ?? config.title,
        resolvedMessage: overrideMessage ?? config.message,
    };
};

export const ErrorDisplay = ({ status = 'error', title, message, onRetry }: ErrorDisplayProps) => {
    const theme = useTheme();

    const { config, resolvedTitle, resolvedMessage } = resolveConfig(status, { title, message });
    const Icon = config.icon;
    const accent = config.tone === 'error' ? theme.palette.error.main : theme.palette.text.secondary;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 2,
                pr: 4,
                pl: 4,
                maxWidth: 400,
                mx: 'auto',
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: config.tone === 'error' ? `${accent}20` : theme.palette.action.selected,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Icon sx={{ fontSize: 40, color: accent }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {resolvedTitle}
            </Typography>

            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                {resolvedMessage}
            </Typography>

            {onRetry && status === 'error' && (
                <Button variant="outlined" onClick={onRetry} sx={{ mt: 2 }}>
                    Try Again
                </Button>
            )}
        </Box>
    );
};

export default ErrorDisplay;
